import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getTenant } from "../lib/tenant";
import {
    Map,
    LayoutDashboard,
    Rocket,
    Send,
    Images,
    Globe,
    Crown,
    LogOut,
    ShieldCheck,
    Compass,
    BookOpenText,
    Inbox,
} from "lucide-react";
import SovereignAnchor from "./SovereignAnchor";

const ALL_NAV_ITEMS = [
    { key: "map", to: "/app/map", label: "Territorial Map", icon: Map, testid: "nav-map" },
    { key: "boarding", to: "/app/boarding", label: "Partner Boarding", icon: Rocket, testid: "nav-boarding" },
    { key: "dashboard", to: "/app/dashboard", label: "Transparency", icon: LayoutDashboard, testid: "nav-dashboard" },
    { key: "scout", to: "/app/scout", label: "Scout Reports", icon: Send, testid: "nav-scout" },
    { key: "adkits", to: "/app/ad-kits", label: "Ad Kit Repository", icon: Images, testid: "nav-adkits" },
    { key: "subdomains", to: "/app/subdomains", label: "Subdomains", icon: Globe, testid: "nav-subdomains" },
];

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const isDirector = user && user.role === "director";
    const tenant = getTenant();
    const navItems = ALL_NAV_ITEMS.filter((n) => tenant.modules.includes(n.key));

    return (
        <div className="min-h-screen flex bg-background grain-overlay relative">
            {/* Sidebar */}
            <aside className="hidden md:flex w-64 flex-col glass border-r border-[hsl(40_40%_30%/0.2)] sticky top-0 h-screen z-10">
                <div className="p-6 border-b border-[hsl(40_40%_30%/0.18)]">
                    <div
                        className="flex items-center gap-3 cursor-pointer"
                        onClick={() => navigate("/")}
                        data-testid="sidebar-logo"
                    >
                        <SovereignAnchor
                            size={34}
                            strokeWidth={6}
                            className="text-[hsl(45_85%_68%)] drop-shadow-[0_2px_6px_hsl(40_70%_45%/0.25)]"
                        />
                        <div className="flex flex-col leading-none">
                            <span className="font-heading text-3xl tracking-tight gold-gradient-text">
                                EIGOO
                            </span>
                            <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mt-1">
                                {tenant.id === "default" ? "Fleet Command" : tenant.name.replace("EIGOO ", "") + " Bridge"}
                            </span>
                        </div>
                    </div>
                </div>

                <nav className="flex-1 px-3 py-4 space-y-1">
                    <NavLink
                        to="/bridge"
                        data-testid="nav-bridge"
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                                isActive
                                    ? "bg-[hsl(40_61%_25%/0.45)] text-[hsl(45_85%_75%)]"
                                    : "text-[hsl(45_85%_72%)] hover:bg-[hsl(151_55%_15%)]"
                            }`
                        }
                    >
                        <Compass size={16} strokeWidth={1.5} />
                        <span>Master Bridge</span>
                    </NavLink>
                    <div className="my-2 mx-3 border-t border-[hsl(40_40%_30%/0.12)]" />
                    {navItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={item.testid}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                                    isActive
                                        ? "bg-[hsl(40_61%_25%/0.45)] text-[hsl(45_85%_75%)]"
                                        : "text-muted-foreground hover:bg-[hsl(151_55%_15%)] hover:text-foreground"
                                }`
                            }
                        >
                            <item.icon size={16} strokeWidth={1.5} />
                            <span>{item.label}</span>
                        </NavLink>
                    ))}

                    {isDirector && (
                        <>
                            <div className="my-4 mx-3 border-t border-[hsl(40_40%_30%/0.15)]" />
                            <NavLink
                                to="/app/director"
                                data-testid="nav-director"
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                                        isActive
                                            ? "bg-[hsl(40_61%_25%/0.45)] text-[hsl(45_85%_75%)]"
                                            : "text-muted-foreground hover:bg-[hsl(151_55%_15%)] hover:text-foreground"
                                    }`
                                }
                            >
                                <Crown size={16} strokeWidth={1.5} />
                                <span>Director Control</span>
                            </NavLink>
                            <NavLink
                                to="/app/director/boarding"
                                data-testid="nav-director-boarding"
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                                        isActive
                                            ? "bg-[hsl(40_61%_25%/0.45)] text-[hsl(45_85%_75%)]"
                                            : "text-muted-foreground hover:bg-[hsl(151_55%_15%)] hover:text-foreground"
                                    }`
                                }
                            >
                                <Crown size={16} strokeWidth={1.5} />
                                <span>Foundry Boarding</span>
                            </NavLink>
                            <NavLink
                                to="/app/director/hardware"
                                data-testid="nav-director-hardware"
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                                        isActive
                                            ? "bg-[hsl(40_61%_25%/0.45)] text-[hsl(45_85%_75%)]"
                                            : "text-muted-foreground hover:bg-[hsl(151_55%_15%)] hover:text-foreground"
                                    }`
                                }
                            >
                                <Crown size={16} strokeWidth={1.5} />
                                <span>Fleet Flasher</span>
                            </NavLink>
                            <NavLink
                                to="/app/director/forensics"
                                data-testid="nav-director-forensics"
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                                        isActive
                                            ? "bg-[hsl(40_61%_25%/0.45)] text-[hsl(45_85%_75%)]"
                                            : "text-muted-foreground hover:bg-[hsl(151_55%_15%)] hover:text-foreground"
                                    }`
                                }
                            >
                                <Crown size={16} strokeWidth={1.5} />
                                <span>Sovereign Forensics</span>
                            </NavLink>
                            <NavLink
                                to="/app/leads"
                                data-testid="nav-director-leads"
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                                        isActive
                                            ? "bg-[hsl(40_61%_25%/0.45)] text-[hsl(45_85%_75%)]"
                                            : "text-muted-foreground hover:bg-[hsl(151_55%_15%)] hover:text-foreground"
                                    }`
                                }
                            >
                                <Inbox size={16} strokeWidth={1.5} />
                                <span>Lead Inbox</span>
                            </NavLink>
                        </>
                    )}

                    <div className="my-4 mx-3 border-t border-[hsl(40_40%_30%/0.15)]" />
                    <NavLink
                        to="/intel"
                        data-testid="nav-intel"
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                                isActive
                                    ? "bg-[hsl(40_61%_25%/0.45)] text-[hsl(45_85%_75%)]"
                                    : "text-muted-foreground hover:bg-[hsl(151_55%_15%)] hover:text-foreground"
                            }`
                        }
                    >
                        <BookOpenText size={16} strokeWidth={1.5} />
                        <span>Fleet Intel</span>
                    </NavLink>
                </nav>

                <div className="p-4 border-t border-[hsl(40_40%_30%/0.15)]">
                    <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-full bg-[hsl(40_60%_45%)] flex items-center justify-center text-[hsl(40_20%_10%)] font-semibold text-xs">
                            {user?.name?.[0]?.toUpperCase() || "P"}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm text-foreground truncate">{user?.name}</div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                                {isDirector && <ShieldCheck size={10} />}
                                {user?.role}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={async () => {
                            await logout();
                            navigate("/");
                        }}
                        data-testid="btn-logout"
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs text-muted-foreground hover:bg-[hsl(350_50%_25%/0.3)] hover:text-[hsl(0_0%_98%)] transition-colors"
                    >
                        <LogOut size={14} />
                        Disembark
                    </button>
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 relative z-[2]">
                {/* Mobile top bar */}
                <div className="md:hidden flex items-center justify-between px-4 py-3 border-b border-[hsl(40_40%_30%/0.15)]">
                    <div className="flex items-center gap-2">
                        <SovereignAnchor size={22} strokeWidth={6} className="text-[hsl(45_85%_68%)]" />
                <div className="font-heading text-xl gold-gradient-text">EIGOO</div>
                    </div>
                    <button
                        onClick={async () => { await logout(); navigate("/"); }}
                        className="text-xs text-muted-foreground"
                        data-testid="btn-logout-mobile"
                    >
                        Disembark
                    </button>
                </div>
                <div className="p-6 md:p-10 max-w-[1600px] mx-auto">{children}</div>
            </main>
        </div>
    );
}
